import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
	Button,
	Divider,
	Flex,
	Group,
	Loader,
	Modal,
	Progress,
	ScrollArea,
	SegmentedControl,
	Select,
	Stack,
	Table,
	Text,
	TextInput,
} from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import {
	BulkAddCounterpartyJobStatus,
	type BulkAddCounterpartyJobStatusRes,
	type BulkAddCounterpartyPreviewRes,
} from '@dealstack/shared/lib/generated';
import {
	SLOT_MODE,
	type AddCounterpartyFormValues,
	type AssignableUser,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/AddCounterpartyModal.types';
import {
	addCounterpartySlotValidation,
	emptySlot,
	toSlotSubmissions,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/AddCounterpartyModal.utils';
import {
	BULK_MAX_COUNTERPARTY_SLOTS,
	type BulkAddCounterpartyDetails,
} from './BulkAddCounterpartyModal.types';

interface Props {
	preview: BulkAddCounterpartyPreviewRes | null;
	isLoadingPreview: boolean;
	isPreviewError: boolean;
	assignableUsers: AssignableUser[];
	isSubmitting: boolean;
	jobId: string | null;
	jobStatus: BulkAddCounterpartyJobStatusRes | null;
	onPreview: (details: BulkAddCounterpartyDetails) => void;
	onConfirm: (details: BulkAddCounterpartyDetails) => void;
	onClose: () => void;
}

export const BulkAddCounterpartyModal = ({
	preview,
	isLoadingPreview,
	isPreviewError,
	assignableUsers,
	isSubmitting,
	jobId,
	jobStatus,
	onPreview,
	onConfirm,
	onClose,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const [step, setStep] = useState<'details' | 'preview'>('details');
	const [submittedDetails, setSubmittedDetails] = useState<BulkAddCounterpartyDetails | null>(
		null
	);

	const schema = z
		.object({
			name: z
				.string()
				.trim()
				.min(1, tMipManage('documents_page.bulk_add_counterparty_modal.name_required'))
				.max(255, tMipManage('documents_page.bulk_add_counterparty_modal.name_too_long')),
			slotCount: z.number().int().min(1).max(BULK_MAX_COUNTERPARTY_SLOTS),
			slots: z.array(
				z.object({
					mode: z.enum(['UNASSIGNED', 'USER', 'INVITE']),
					userId: z.string(),
					firstName: z.string(),
					lastName: z.string(),
					email: z.string(),
				})
			),
		})
		.superRefine(addCounterpartySlotValidation(tMipManage));

	const form = useForm<AddCounterpartyFormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			name: '',
			slotCount: 1,
			slots: [emptySlot(), emptySlot()],
		},
	});

	const slotCount = form.watch('slotCount');
	const slotModes = form.watch('slots').map((slot) => slot.mode);

	const handleClose = () => {
		form.reset();
		setStep('details');
		setSubmittedDetails(null);
		onClose();
	};

	const handlePreviewSubmit = form.handleSubmit((values) => {
		const details: BulkAddCounterpartyDetails = {
			name: values.name.trim(),
			slots: toSlotSubmissions(values),
		};
		setSubmittedDetails(details);
		setStep('preview');
		onPreview(details);
	});

	const handleConfirm = () => {
		if (!submittedDetails) {
			return;
		}
		onConfirm(submittedDetails);
	};

	const slotCountOptions = Array.from({ length: BULK_MAX_COUNTERPARTY_SLOTS }, (_, index) => ({
		value: String(index + 1),
		label: String(index + 1),
	}));

	const renderSkipReasonTable = (
		groups: { reason: string; message: string; count: number }[]
	) => (
		<Table
			withTableBorder
			layout="fixed"
			data-testid={TEST_ELEMENT_IDS.BULK_ADD_COUNTERPARTY_SKIP_TABLE}
		>
			<Table.Thead>
				<Table.Tr>
					<Table.Th w={80}>
						{tMipManage(
							'documents_page.bulk_add_counterparty_modal.skip_table_count_header'
						)}
					</Table.Th>
					<Table.Th>
						{tMipManage(
							'documents_page.bulk_add_counterparty_modal.skip_table_reason_header'
						)}
					</Table.Th>
				</Table.Tr>
			</Table.Thead>
			<Table.Tbody>
				{groups.map((group) => (
					<Table.Tr key={group.reason}>
						<Table.Td fw={600}>{group.count}</Table.Td>
						<Table.Td>{group.message}</Table.Td>
					</Table.Tr>
				))}
			</Table.Tbody>
		</Table>
	);

	const renderDetails = () => (
		<Stack gap="sm" p="md">
			<Controller
				control={form.control}
				name="name"
				render={({ field, fieldState }) => (
					<TextInput
						{...field}
						data-testid={TEST_ELEMENT_IDS.BULK_ADD_COUNTERPARTY_NAME_INPUT}
						label={tMipManage('documents_page.bulk_add_counterparty_modal.name_label')}
						error={fieldState.error?.message}
					/>
				)}
			/>

			<Controller
				control={form.control}
				name="slotCount"
				render={({ field }) => (
					<Select
						data-testid={TEST_ELEMENT_IDS.BULK_ADD_COUNTERPARTY_SLOT_COUNT_SELECT}
						label={tMipManage(
							'documents_page.bulk_add_counterparty_modal.slot_count_label'
						)}
						data={slotCountOptions}
						value={String(field.value)}
						onChange={(value) => field.onChange(Number(value ?? 1))}
						allowDeselect={false}
					/>
				)}
			/>

			{Array.from({ length: slotCount }, (_, index) => index).map((index) => (
				<Stack key={index} gap="xs">
					<Divider />
					<Text size="sm" fw={500}>
						{tMipManage('documents_page.bulk_add_counterparty_modal.slot_label', {
							index: index + 1,
						})}
					</Text>

					<Controller
						control={form.control}
						name={`slots.${index}.mode`}
						render={({ field }) => (
							<SegmentedControl
								fullWidth
								data-testid={TEST_ELEMENT_IDS.BULK_ADD_COUNTERPARTY_SLOT_MODE}
								value={field.value}
								onChange={field.onChange}
								data={[
									{
										value: SLOT_MODE.UNASSIGNED,
										label: tMipManage(
											'documents_page.bulk_add_counterparty_modal.mode_unassigned'
										),
									},
									{
										value: SLOT_MODE.USER,
										label: tMipManage(
											'documents_page.bulk_add_counterparty_modal.mode_user'
										),
									},
									{
										value: SLOT_MODE.INVITE,
										label: tMipManage(
											'documents_page.bulk_add_counterparty_modal.mode_invite'
										),
									},
								]}
							/>
						)}
					/>

					{slotModes[index] === SLOT_MODE.USER && (
						<Controller
							control={form.control}
							name={`slots.${index}.userId`}
							render={({ field, fieldState }) => (
								<Select
									label={tMipManage(
										'documents_page.bulk_add_counterparty_modal.user_label'
									)}
									searchable
									data={assignableUsers.map((user) => ({
										value: user.id,
										label: user.label,
									}))}
									value={field.value || null}
									onChange={(value) => field.onChange(value ?? '')}
									error={fieldState.error?.message}
								/>
							)}
						/>
					)}

					{slotModes[index] === SLOT_MODE.INVITE && (
						<>
							<Group grow align="flex-start">
								<Controller
									control={form.control}
									name={`slots.${index}.firstName`}
									render={({ field, fieldState }) => (
										<TextInput
											{...field}
											label={tMipManage(
												'documents_page.bulk_add_counterparty_modal.first_name_label'
											)}
											error={fieldState.error?.message}
										/>
									)}
								/>
								<Controller
									control={form.control}
									name={`slots.${index}.lastName`}
									render={({ field, fieldState }) => (
										<TextInput
											{...field}
											label={tMipManage(
												'documents_page.bulk_add_counterparty_modal.last_name_label'
											)}
											error={fieldState.error?.message}
										/>
									)}
								/>
							</Group>
							<Controller
								control={form.control}
								name={`slots.${index}.email`}
								render={({ field, fieldState }) => (
									<TextInput
										{...field}
										label={tMipManage(
											'documents_page.bulk_add_counterparty_modal.email_label'
										)}
										error={fieldState.error?.message}
									/>
								)}
							/>
						</>
					)}
				</Stack>
			))}

			<Group justify="right" gap="xs">
				<Button variant="default" onClick={handleClose}>
					{tMipManage('documents_page.bulk_add_counterparty_modal.cancel_button')}
				</Button>
				<Button
					data-testid={TEST_ELEMENT_IDS.BULK_ADD_COUNTERPARTY_PREVIEW_BUTTON}
					onClick={() => void handlePreviewSubmit()}
				>
					{tMipManage('documents_page.bulk_add_counterparty_modal.preview_button')}
				</Button>
			</Group>
		</Stack>
	);

	const renderPreview = () => {
		if (isPreviewError) {
			return (
				<Stack gap="md" p="xl" align="center">
					<Text size="sm" c="red">
						{tMipManage('documents_page.bulk_add_counterparty_modal.preview_error')}
					</Text>
					<Button variant="subtle" onClick={() => setStep('details')}>
						{tMipManage('documents_page.bulk_add_counterparty_modal.back_button')}
					</Button>
				</Stack>
			);
		}

		if (isLoadingPreview || !preview) {
			return (
				<Flex justify="center" align="center" p="xl">
					<Loader />
				</Flex>
			);
		}

		return (
			<Stack gap="md" p="md">
				<Text size="sm">
					{tMipManage('documents_page.bulk_add_counterparty_modal.description', {
						eligible: preview.eligibleCount,
						total: preview.totalSelected,
					})}
				</Text>
				{preview.evaluationTruncated && (
					<Text size="xs" c="dimmed">
						{tMipManage(
							'documents_page.bulk_add_counterparty_modal.evaluation_truncated',
							{
								evaluated: preview.evaluated,
								total: preview.totalSelected,
							}
						)}
					</Text>
				)}
				{preview.inviteDowngradedCount > 0 && (
					<Text size="xs" c="orange">
						{tMipManage(
							'documents_page.bulk_add_counterparty_modal.invite_downgraded',
							{
								count: preview.inviteDowngradedCount,
							}
						)}
					</Text>
				)}
				{preview.skipped.length > 0 && (
					<ScrollArea.Autosize mah={300}>
						{renderSkipReasonTable(preview.skipped)}
					</ScrollArea.Autosize>
				)}
				<Group justify="right" gap="xs">
					<Button
						variant="subtle"
						onClick={() => setStep('details')}
						disabled={isSubmitting}
					>
						{tMipManage('documents_page.bulk_add_counterparty_modal.back_button')}
					</Button>
					<Button
						data-testid={TEST_ELEMENT_IDS.BULK_ADD_COUNTERPARTY_CONFIRM_BUTTON}
						disabled={preview.eligibleCount === 0 || isSubmitting || jobId != null}
						loading={isSubmitting}
						onClick={handleConfirm}
					>
						{tMipManage('documents_page.bulk_add_counterparty_modal.confirm_button')}
					</Button>
				</Group>
			</Stack>
		);
	};

	const renderProgress = (state: BulkAddCounterpartyJobStatusRes) => {
		const percent =
			state.total > 0 ? Math.min(100, Math.round((state.processed / state.total) * 100)) : 0;
		const isTerminal =
			state.status === BulkAddCounterpartyJobStatus.COMPLETED ||
			state.status === BulkAddCounterpartyJobStatus.FAILED;

		return (
			<Stack gap="md" p="md">
				<Text size="sm">
					{tMipManage('documents_page.bulk_add_counterparty_modal.progress_status', {
						processed: state.processed,
						total: state.total,
					})}
				</Text>
				<Progress
					value={percent}
					size="lg"
					color={state.status === BulkAddCounterpartyJobStatus.FAILED ? 'red' : 'blue'}
				/>
				<Text size="xs">
					{tMipManage('documents_page.bulk_add_counterparty_modal.progress_breakdown', {
						added: state.added,
						skipped: state.skipped,
					})}
				</Text>
				{state.skippedByReason.length > 0 && renderSkipReasonTable(state.skippedByReason)}
				{state.errors.length > 0 && (
					<Stack gap={4}>
						{state.errors.map((err, idx) => (
							<Text key={idx} size="xs" c="red">
								{err}
							</Text>
						))}
					</Stack>
				)}
				<Group justify="right">
					<Button
						data-testid={TEST_ELEMENT_IDS.BULK_ADD_COUNTERPARTY_CLOSE_BUTTON}
						onClick={handleClose}
						disabled={!isTerminal}
					>
						{tMipManage('documents_page.bulk_add_counterparty_modal.close_button')}
					</Button>
				</Group>
			</Stack>
		);
	};

	const modalTitle = jobStatus
		? tMipManage('documents_page.bulk_add_counterparty_modal.results_title')
		: tMipManage('documents_page.bulk_add_counterparty_modal.title');

	const renderContent = () => {
		if (jobStatus) {
			return renderProgress(jobStatus);
		}
		if (step === 'preview') {
			return renderPreview();
		}
		return renderDetails();
	};

	return (
		<Modal.Root opened onClose={handleClose} centered size="lg">
			<Modal.Overlay />
			{/* data-testid goes on Content, not Root: Root has no box of its own, so a Playwright
			    visibility check against it never resolves even while the modal is genuinely open. */}
			<Modal.Content data-testid={TEST_ELEMENT_IDS.BULK_ADD_COUNTERPARTY_MODAL}>
				<Modal.Header>
					<Modal.Title>{modalTitle}</Modal.Title>
					<Modal.CloseButton />
				</Modal.Header>
				<Modal.Body>{renderContent()}</Modal.Body>
			</Modal.Content>
		</Modal.Root>
	);
};
